import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type {
  BreakdownCheck,
  BreakdownColor,
  BreakdownFormula,
  BreakdownTerm,
  FToken,
} from '../lib/breakdown';

/**
 * Иконка «ƒ» + поповер-разбор «как получилась цифра» (гибрид A+C):
 * сверху формула с подстановкой, снизу чек-строки-легенда с раскрывашками,
 * цвет и hover связывают всё, включая подсветку реальных полей формы.
 *
 * Связь с полями: у term.fieldRef ищется элемент `[data-fh="<ref>"]` —
 * hover добавляет ему .fh-glow, клик фокусирует input внутри.
 *
 * Portal к document.body — иначе поповер режется overflow-hidden модалки
 * (тот же паттерн, что Combobox/DatePicker).
 */

const COLOR_TOK: Record<BreakdownColor, string> = {
  g: 'text-success bg-[var(--green-bg)] border-[var(--green-border)]',
  r: 'text-danger bg-[var(--red-bg)] border-[var(--red-border)]',
  a: 'text-[#d97706] bg-[#fffbeb] border-[#fde68a]',
  v: 'text-[#7c3aed] bg-[#f5f3ff] border-[#ddd6fe]',
  b: 'text-accent bg-[var(--accent-bg)] border-[var(--accent-border)]',
};
const COLOR_DOT: Record<BreakdownColor, string> = {
  g: 'bg-success',
  r: 'bg-danger',
  a: 'bg-[#d97706]',
  v: 'bg-[#7c3aed]',
  b: 'bg-accent',
};
const RESULT_TONE: Record<'pos' | 'neg' | 'neu', string> = {
  pos: 'text-success bg-[var(--green-bg)] border-[var(--green-border)]',
  neg: 'text-danger bg-[var(--red-bg)] border-[var(--red-border)]',
  neu: 'text-text bg-bg2 border-border-strong',
};

const BADGE_CLS: Record<string, string> = {
  ввод: 'text-accent bg-[var(--accent-bg)] border-[var(--accent-border)]',
  снапшот: 'text-[#7c3aed] bg-[#f5f3ff] border-[#ddd6fe]',
  настройка: 'text-[#d97706] bg-[#fffbeb] border-[#fde68a]',
  вычислено: 'text-text2 bg-bg2 border-border-strong',
  рынок: 'text-success bg-[var(--green-bg)] border-[var(--green-border)]',
};

interface Props {
  formula: BreakdownFormula;
  /** Обработчик action-кнопок проверок («перевернуть курс»). */
  onCheckAction?: (id: 'flip-cl' | 'flip-ex') => void;
  /** aria-label иконки. */
  label?: string;
}

export function FormulaHint({ formula, onCheckAction, label = 'Как посчитано?' }: Props) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        tabIndex={-1}
        aria-label={label}
        title={label}
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border font-mono text-[10px] font-bold transition-colors duration-150 ${
          open
            ? 'border-accent bg-accent text-white'
            : 'border-[var(--accent-border)] bg-[var(--accent-bg)] text-accent hover:bg-accent hover:text-white'
        }`}
      >
        ƒ
      </button>
      {open && (
        <HintPopover
          anchor={btnRef}
          formula={formula}
          onClose={() => setOpen(false)}
          onCheckAction={onCheckAction}
        />
      )}
    </>
  );
}

function HintPopover({
  anchor,
  formula,
  onClose,
  onCheckAction,
}: {
  anchor: React.RefObject<HTMLButtonElement>;
  formula: BreakdownFormula;
  onClose: () => void;
  onCheckAction?: (id: 'flip-cl' | 'flip-ex') => void;
}) {
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [hoverK, setHoverK] = useState<string | null>(null);
  const glowRef = useRef<Element | null>(null);

  // Позиционирование: под иконкой, прижато к правому краю если не влезает.
  useLayoutEffect(() => {
    const btn = anchor.current;
    const pop = popRef.current;
    if (!btn || !pop) return;
    const r = btn.getBoundingClientRect();
    const w = pop.offsetWidth;
    const h = pop.offsetHeight;
    let left = r.left;
    if (left + w > window.innerWidth - 12) left = Math.max(12, window.innerWidth - w - 12);
    let top = r.bottom + 6;
    if (top + h > window.innerHeight - 12) top = Math.max(12, r.top - h - 6);
    setPos({ top, left });
  }, [anchor]);

  // Закрытие: Escape / клик вне (как Combobox; это поповер, не модалка).
  // Escape ловим в capture-фазе со stopPropagation — иначе window-слушатель
  // Modal закроет ВСЮ модалку вместе с поповером одним нажатием.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (popRef.current?.contains(t)) return;
      if (anchor.current?.contains(t)) return;
      onClose();
    }
    document.addEventListener('keydown', onKey, { capture: true });
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey, { capture: true });
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose, anchor]);

  const clearGlow = useCallback(() => {
    glowRef.current?.classList.remove('fh-glow', 'fh-glow-g', 'fh-glow-r', 'fh-glow-a', 'fh-glow-v', 'fh-glow-b');
    glowRef.current = null;
  }, []);

  // Подсветка поля — тем же цветом, что и терм в разборе (см. .fh-glow-* в index.css).
  const hoverTerm = useCallback(
    (k: string | null, fieldRef?: string, color?: BreakdownColor) => {
      setHoverK(k);
      clearGlow();
      if (k && fieldRef) {
        const el = document.querySelector(`[data-fh="${fieldRef}"]`);
        if (el) {
          el.classList.add('fh-glow', `fh-glow-${color ?? 'b'}`);
          glowRef.current = el;
        }
      }
    },
    [clearGlow],
  );
  // Снять подсветку поля при размонтировании поповера.
  useEffect(() => clearGlow, [clearGlow]);

  const focusField = useCallback((fieldRef?: string) => {
    if (!fieldRef) return;
    const el = document.querySelector(`[data-fh="${fieldRef}"]`);
    const input = el?.querySelector<HTMLInputElement>('input');
    if (input) {
      input.focus();
      input.select();
    }
  }, []);

  const fieldRefByK = new Map(formula.terms.map((t) => [t.k, t.fieldRef] as const));
  const colorByK = new Map(formula.terms.map((t) => [t.k, t.color] as const));

  function renderToken(t: FToken, i: number) {
    if (!t.k) {
      return (
        <span key={i} className="text-text-subtle">
          {t.text}
        </span>
      );
    }
    const hl = hoverK === t.k;
    return (
      <span
        key={i}
        onMouseEnter={() => hoverTerm(t.k!, fieldRefByK.get(t.k!), colorByK.get(t.k!) ?? t.color)}
        onMouseLeave={() => hoverTerm(null)}
        onClick={() => focusField(fieldRefByK.get(t.k!))}
        className={`cursor-default rounded-md border px-1.5 transition-shadow duration-100 ${COLOR_TOK[t.color ?? 'b']} ${
          hl ? 'shadow-[0_0_0_2px_currentColor]' : ''
        }`}
      >
        {t.text}
      </span>
    );
  }

  return createPortal(
    <div
      ref={popRef}
      style={pos ? { top: pos.top, left: pos.left } : { top: -9999, left: -9999 }}
      className="fixed z-[80] w-[420px] max-w-[calc(100vw-24px)] overflow-hidden rounded-xl border border-border-strong bg-bg shadow-[0_12px_32px_rgba(15,23,42,0.16)]"
      role="dialog"
      aria-label={formula.title}
    >
      <div className="flex items-center justify-between border-b border-border bg-bg2 px-3.5 py-2">
        <span className="text-[11px] font-bold uppercase tracking-wide text-text2">{formula.title}</span>
        <button type="button" onClick={onClose} aria-label="Закрыть" className="text-text-subtle hover:text-text">
          ✕
        </button>
      </div>

      <div className="max-h-[70vh] overflow-y-auto p-3.5">
        {/* Формула с подстановкой */}
        <div className={formula.terms.length ? 'mb-2.5 border-b border-border pb-2.5' : ''}>
          {formula.words.length > 0 && (
            <div className="flex flex-wrap items-center gap-1 py-0.5 font-mono text-[12.5px]">
              {formula.words.map(renderToken)}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-1 py-0.5 font-mono text-[13px] tabular-nums">
            <span className="text-text-subtle">=</span>
            {formula.numbers.map(renderToken)}
            <span className="text-text-subtle">=</span>
            <span className={`rounded-md border px-1.5 font-bold ${RESULT_TONE[formula.result.tone]}`}>
              {formula.result.display}
            </span>
          </div>
          {formula.round && (
            <div className="pt-0.5 font-mono text-[10.5px] tabular-nums text-text2">
              точно: <span className="font-semibold text-text">{formula.round.exact}</span> — {formula.round.note}
            </div>
          )}
        </div>

        {/* Чек-строки (легенда) */}
        {formula.terms.map((t) => (
          <TermRow
            key={t.k}
            term={t}
            hl={hoverK === t.k}
            onHover={(on) => hoverTerm(on ? t.k : null, t.fieldRef, t.color)}
            onClick={() => focusField(t.fieldRef)}
            renderToken={renderToken}
          />
        ))}

        {/* Проверки */}
        {formula.checks && formula.checks.length > 0 && (
          <div className="mt-2.5 overflow-hidden rounded-lg border border-border">
            {formula.checks.map((c, i) => (
              <CheckRow key={i} check={c} onAction={onCheckAction} />
            ))}
          </div>
        )}
      </div>

      {formula.notes && formula.notes.length > 0 && (
        <div className="border-t border-border bg-bg2 px-3.5 py-2 text-[10.5px] leading-snug text-text2">
          {formula.notes.map((n, i) => (
            <div key={i}>{n}</div>
          ))}
        </div>
      )}
    </div>,
    document.body,
  );
}

function TermRow({
  term: t,
  hl,
  onHover,
  onClick,
  renderToken,
}: {
  term: BreakdownTerm;
  hl: boolean;
  onHover: (on: boolean) => void;
  onClick: () => void;
  renderToken: (t: FToken, i: number) => React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border-t border-[color:var(--border-subtle,#eef2f6)] first:border-t-0">
      <div
        onMouseEnter={() => onHover(true)}
        onMouseLeave={() => onHover(false)}
        onClick={() => (t.sub ? setExpanded((v) => !v) : onClick())}
        className={`grid cursor-pointer grid-cols-[12px_1fr_auto] items-baseline gap-2 rounded-lg px-1 py-1.5 ${
          hl ? 'bg-bg2' : ''
        }`}
      >
        <span className={`h-[9px] w-[9px] self-center justify-self-center rounded-full ${COLOR_DOT[t.color]}`} />
        <span className="text-[12.5px] leading-snug text-text">
          {t.label}{' '}
          <span
            className={`inline-flex items-center rounded-full border px-1.5 text-[9.5px] font-semibold align-[1px] ${BADGE_CLS[t.badge]}`}
          >
            {t.badge}
          </span>
          {t.badgeNote && (
            <span className={`ml-1 inline-flex items-center rounded-full border px-1.5 text-[9.5px] font-semibold align-[1px] ${BADGE_CLS['рынок']}`}>
              {t.badgeNote}
            </span>
          )}
          {t.sub && (
            <span className="ml-1.5 text-[9.5px] font-semibold text-accent">
              {expanded ? '▾ свернуть' : '▸ расчёт'}
            </span>
          )}
          {!t.sub && t.fieldRef && hl && (
            <span className="ml-1.5 text-[9.5px] font-semibold text-accent">клик → к полю</span>
          )}
        </span>
        <span className="font-mono text-[12.5px] font-semibold tabular-nums">{t.value}</span>
      </div>
      {t.sub && expanded && (
        <div className="mb-1.5 ml-[19px] rounded-r-lg border-l-2 border-[var(--accent-border)] bg-bg2 px-2.5 py-1.5">
          <div className="flex flex-wrap items-center gap-1 font-mono text-[12px] tabular-nums">
            {t.sub.numbers.map(renderToken)}
          </div>
          {t.sub.note && <div className="mt-0.5 text-[10.5px] leading-snug text-text2">{t.sub.note}</div>}
        </div>
      )}
    </div>
  );
}

function CheckRow({
  check: c,
  onAction,
}: {
  check: BreakdownCheck;
  onAction?: (id: 'flip-cl' | 'flip-ex') => void;
}) {
  const cls =
    c.level === 'ok'
      ? 'bg-[var(--green-bg)]'
      : c.level === 'warn'
        ? 'bg-[#fffbeb]'
        : 'bg-[var(--red-bg)]';
  const ic = c.level === 'ok' ? '✓' : c.level === 'warn' ? '⚠' : '✕';
  const icCls = c.level === 'ok' ? 'text-success' : c.level === 'warn' ? 'text-[#d97706]' : 'text-danger';
  return (
    <div className={`flex items-start gap-2 border-t border-border px-2.5 py-2 text-[12px] leading-snug first:border-t-0 ${cls}`}>
      <span className={`font-bold ${icCls}`}>{ic}</span>
      <span className="min-w-0">
        {c.text}
        {c.action && onAction && (
          <button
            type="button"
            onClick={() => onAction(c.action!.id)}
            className="mt-1.5 block rounded-md border border-[var(--accent-border)] bg-bg px-2.5 py-1 text-[12px] font-semibold text-accent transition-colors duration-150 hover:bg-accent hover:text-white"
          >
            {c.action.label}
          </button>
        )}
      </span>
    </div>
  );
}
